/**
 * PublicTrackingPage — customer-facing live tracking (/t/:token).
 * No auth. Tenant branding + live bus map + planned-stop timeline.
 * Polls every ~15 s when active, backs off to 60 s when the tab is hidden
 * (protects Maps quota on forgotten tabs). Friendly states before tracking
 * starts / after the trip is completed / when the link is not yet active,
 * expired or revoked — never a blank map or bare error.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, MapPin, Bus, Clock, User, XCircle, CalendarClock, CheckCircle2,
} from 'lucide-react';
import PublicTrackingMap from '@/components/public/PublicTrackingMap';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

// Dato/klokke følger sidens språk (nb-NO / en-GB) — kun presentasjon.
const localeOf = (lang: string) => (lang === 'en' ? 'en-GB' : 'nb-NO');
const fmtDate = (d?: string | null, lang = 'no') => {
  if (!d) return null;
  try { return new Date(d + 'T00:00:00').toLocaleDateString(localeOf(lang), { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
};
const fmtWhen = (iso?: string | null, lang = 'no') => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(localeOf(lang), { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtClock = (iso?: string | null, lang = 'no') => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString(localeOf(lang), { hour: '2-digit', minute: '2-digit' }); }
  catch { return null; }
};

function Shell({ children, testid, toggle }: { children: React.ReactNode; testid: string; toggle?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center relative" data-testid={testid}>
        {toggle && <div className="absolute right-3 top-3">{toggle}</div>}
        {children}
      </div>
    </div>
  );
}

export default function PublicTrackingPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(data?.preferred_language);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const timerRef = useRef<any>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/tracking/${token}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) return;
      setData(await res.json());
    } catch {
      /* keep last data on transient network error */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      await load();
      if (cancelled) return;
      const hidden = document.visibilityState === 'hidden';
      const active = (dataStateRef.current === 'active');
      const delay = hidden ? 60000 : active ? 15000 : 30000;
      timerRef.current = setTimeout(tick, delay);
    };
    tick();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timerRef.current);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearTimeout(timerRef.current); document.removeEventListener('visibilitychange', onVis); };
  }, [token]);

  // Keep latest state for the polling closure without re-arming the effect.
  const dataStateRef = useRef<string>('');
  useEffect(() => { dataStateRef.current = data?.state || ''; }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" data-testid="tracking-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <Shell testid="tracking-not-found" toggle={<PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />}>
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicTrackingPage.sporingslenkenErIkkeGyldig', 'Sporingslenken er ikke gyldig')}</h1>
        <p className="text-sm text-slate-500">{t('publicTrackingPage.lenkenKanVaereFeilEller', 'Lenken kan være feil eller utløpt. Kontakt oss hvis du mener dette er en feil.')}</p>
      </Shell>
    );
  }

  const company = data?.company || {};
  const brandHeader = (
    <div className="text-center mb-5 relative">
      <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} className="absolute right-0 top-0" />
      {company.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-2 object-contain" />}
      <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-testid="tracking-company">{company.name}</p>
    </div>
  );

  if (data?.state === 'expired') {
    return (
      <Shell testid="tracking-expired" toggle={<PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />}>
        <CalendarClock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicTrackingPage.sporingslenkenErUtlopt', 'Sporingslenken er utløpt')}</h1>
        <p className="text-sm text-slate-500">{t('publicTrackingPage.denneTurenErFerdigEller', 'Denne turen er ferdig, eller lenken har gått ut på tid.')}</p>
      </Shell>
    );
  }
  if (data?.state === 'revoked') {
    return (
      <Shell testid="tracking-revoked" toggle={<PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />}>
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicTrackingPage.sporingslenkenErDeaktivert', 'Sporingslenken er deaktivert')}</h1>
        <p className="text-sm text-slate-500">{t('publicTrackingPage.kontaktOssHvisDuFortsatt', 'Kontakt oss hvis du fortsatt trenger å følge turen.')}</p>
      </Shell>
    );
  }
  if (data?.state === 'not_yet_active') {
    return (
      <Shell testid="tracking-not-yet-active" toggle={<PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />}>
        <CalendarClock className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicTrackingPage.sporingenErIkkeApnetEnna', 'Sporingen er ikke åpnet ennå')}</h1>
        <p className="text-sm text-slate-500">
          {t('publicTrackingPage.liveSporingApner', 'Live-sporing åpner')} {data.valid_from ? <strong>{fmtWhen(data.valid_from, lang)}</strong> : t('publicTrackingPage.kortTidForAvreise', 'kort tid før avreise')} — {t('publicTrackingPage.komTilbakeDaForAFolge', 'kom tilbake da for å følge bussen.')}
        </p>
      </Shell>
    );
  }

  // state === 'active'
  const status = data?.status;
  const pos = data?.position;
  const stops = data?.planned_stops || [];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="tracking-page">
      <div className="max-w-lg mx-auto">
        {brandHeader}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Trip header */}
          <div className="p-5 border-b border-slate-100 space-y-2">
            {(data.from_location || data.to_location) && (
              <div className="flex items-center gap-3 text-slate-800 font-medium">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span data-testid="tracking-route">{data.from_location}{data.to_location ? ` → ${data.to_location}` : ''}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-slate-600 text-sm">
              <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{fmtDate(data.departure_date, lang)}{data.departure_time ? ` · ${t('common.atTime', 'kl.')} ${data.departure_time}` : ''}</span>
            </div>
          </div>

          {/* Status banner */}
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-100" data-testid="tracking-status-banner">
            {status === 'underway' && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> {t('publicTrackingPage.bussenErUnderveis', 'Bussen er underveis')}
              </span>
            )}
            {status === 'not_started' && (
              <span className="inline-flex items-center gap-2 text-sm text-slate-600">
                <Bus className="w-4 h-4 text-slate-400" /> {t('publicTrackingPage.sporingHarIkkeStartetEnna', 'Sporing har ikke startet ennå — du ser bussen her når sjåføren starter turen.')}
              </span>
            )}
            {status === 'completed' && (
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t('publicTrackingPage.turenErFullfort', 'Turen er fullført')}
              </span>
            )}
          </div>

          {/* Map (only when a live position exists) */}
          {status === 'underway' && pos ? (
            <div className="h-80" data-testid="tracking-map-wrap">
              <React.Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}>
                <PublicTrackingMap position={pos} />
              </React.Suspense>
            </div>
          ) : (
            <div className="h-40 flex flex-col items-center justify-center text-center text-slate-400 bg-slate-50" data-testid="tracking-map-placeholder">
              {status === 'completed' ? (
                <><CheckCircle2 className="w-10 h-10 mb-2 text-emerald-400" /><p className="text-sm">{t('publicTrackingPage.turenErFullfortTakkFor', 'Turen er fullført. Takk for at du reiste med oss!')}</p></>
              ) : (
                <><Bus className="w-10 h-10 mb-2" /><p className="text-sm px-6">{t('publicTrackingPage.kartetViserBussensPosisjonSa', 'Kartet viser bussens posisjon så snart turen starter.')}</p></>
              )}
            </div>
          )}

          {/* Driver / vehicle */}
          {(data.driver_first_name || data.vehicle_type) && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-4 text-sm text-slate-600" data-testid="tracking-driver">
              {data.driver_first_name && (
                <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4 text-slate-400" /> {t('publicTrackingPage.sjafor', { defaultValue: 'Sjåfør: {{v1}}', v1: data.driver_first_name })}</span>
              )}
              {data.vehicle_type && (
                <span className="inline-flex items-center gap-1.5"><Bus className="w-4 h-4 text-slate-400" /> {data.vehicle_type}</span>
              )}
              {data.last_updated && <span className="ml-auto text-xs text-slate-400">{t('publicTrackingPage.oppdatert', { defaultValue: 'Oppdatert {{v1}}', v1: fmtClock(data.last_updated, lang) })}</span>}
            </div>
          )}

          {/* Planned stops */}
          {stops.length > 0 && (
            <div className="p-5 border-t border-slate-100" data-testid="tracking-stops">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t('publicTrackingPage.planlagteStopp', 'Planlagte stopp')}</p>
              <ol className="space-y-2">
                {stops.map((s: any, i: number) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700" data-testid={`tracking-stop-${i}`}>
                    <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <span className="flex-1 truncate">{s.location}</span>
                    {s.time && <span className="text-slate-400 tabular-nums">{s.time}</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {(company.phone || company.email) && (
          <p className="text-center text-xs text-slate-400 mt-4">
            {t('public.questions', 'Spørsmål?')} {company.phone && `${t('public.call', 'Ring')} ${company.phone}`}{company.phone && company.email && ` ${t('common.or', 'eller')} `}
            {company.email && `${t('public.sendEmailTo', 'send e-post til')} ${company.email}`}
          </p>
        )}
      </div>
    </div>
  );
}
